import { App, Button, Flex, Typography } from 'antd'
import { Copy } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { CodeSnippet, MonoValue, SectionLabel } from 'src/components'

import { TokenRevealBanner } from '../../components/credentials'

import { PersonalTokenForm } from './PersonalTokenForm'
import {
  buildClientSnippet,
  mcpServerName,
  OAUTH_CLIENTS,
  type McpClientId
} from './snippets'
import type { McpMemberService } from './types'

const { Text } = Typography

type Props = {
  workspaceId: number
  /** Several services share one config; a personal token is issued for exactly one. */
  services: McpMemberService[]
  client: McpClientId
  /** The scopes picked for the first service; a personal token carries them. */
  tokenScopes: Set<string>
  /** A freshly issued PAT, owned by the wizard so it can lock its pickers. */
  token: string | null
  onTokenChange: (token: string | null) => void
}

export const ClientInstructions = ({
  workspaceId,
  services,
  client,
  tokenScopes,
  token,
  onTokenChange
}: Props) => {
  const { t } = useTranslation()
  const { message } = App.useApp()

  const { stepKeys, code } = buildClientSnippet({
    client,
    servers: services.map((s) => ({ service: s.service, url: s.mcp_url })),
    token
  })
  const server = services.map((s) => mcpServerName(s.service)).join(', ')
  const several = services.length > 1

  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text)
      message.success(t('workspace.aiConnectors.connect.copied'))
    } catch {
      message.error(t('workspace.aiConnectors.connect.copyFailed'))
    }
  }

  return (
    <Flex vertical gap={12}>
      <Flex vertical gap={4}>
        <SectionLabel>
          {t(
            several
              ? 'workspace.aiConnectors.connect.mcpUrls'
              : 'workspace.aiConnectors.connect.mcpUrl'
          )}
        </SectionLabel>
        {services.map((s) => (
          <Flex key={s.service} align="center" gap={8}>
            {several ? (
              <Text type="secondary" style={{ minWidth: 96 }}>
                {t(`workspace.aiConnectors.services.${s.service}.name`)}
              </Text>
            ) : null}
            <MonoValue>{s.mcp_url}</MonoValue>
          </Flex>
        ))}
      </Flex>

      <ol style={{ margin: 0, paddingInlineStart: 20 }}>
        {stepKeys.map((key) => (
          <li key={key}>
            <Text>
              {t(`workspace.aiConnectors.connect.steps.${key}`, { server })}
            </Text>
          </li>
        ))}
      </ol>

      {several && !code ? (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {t('workspace.aiConnectors.connect.repeatPerUrl')}
        </Text>
      ) : null}

      {client === 'other' ? (
        token ? (
          <TokenRevealBanner
            token={token}
            onDismiss={() => onTokenChange(null)}
          />
        ) : (
          <PersonalTokenForm
            workspaceId={workspaceId}
            service={services[0]}
            scopes={tokenScopes}
            onIssued={onTokenChange}
          />
        )
      ) : null}

      {code ? (
        <Flex vertical gap={6}>
          <Flex justify="space-between" align="center">
            <SectionLabel>
              {t('workspace.aiConnectors.connect.snippetTitle')}
            </SectionLabel>
            <Button
              size="small"
              icon={<Copy size={14} />}
              onClick={() => void copy(code)}
            >
              {t('workspace.aiConnectors.connect.copy')}
            </Button>
          </Flex>
          <CodeSnippet>{code}</CodeSnippet>
        </Flex>
      ) : null}

      {OAUTH_CLIENTS.has(client) ? (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {t('workspace.aiConnectors.connect.oauthHint')}
        </Text>
      ) : null}

      {client === 'other' && !token ? (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {t('workspace.aiConnectors.pat.placeholderHint')}
        </Text>
      ) : null}
    </Flex>
  )
}
